import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { isDeletedRecord, useAuditHighlight } from "@/lib/auditHighlight";
import { AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { listSettings, updateSetting } from "@/services/api/settings";
import type { AppSetting } from "@/services/api/types";
import { MailboxesSection } from "./MailboxesSection";
import { RestartSection } from "./RestartSection";

/** Settings holding credentials — shown masked with a reveal toggle. */
const SECRET_KEYS = new Set([
  "translation.api_key",
  "translation.credentials_path",
  "maps.api_key",
]);

function SettingRow({ setting, highlighted }: { setting: AppSetting; highlighted: boolean }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(setting.value);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(false);
  const isSecret = SECRET_KEYS.has(setting.key);

  const save = async () => {
    if (value === setting.value) return;
    setSaving(true);
    try {
      await updateSetting(setting.key, value);
      toast.success("Setting saved");
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
    } catch (error) {
      toast.error(errorMessage(error));
      setValue(setting.value);
    } finally {
      setSaving(false);
    }
  };

  return (
    // The thick yellow box marks the setting an audit deep-link pointed at.
    <div
      className={`flex items-start justify-between gap-4 border-b py-3 last:border-0${
        highlighted ? " audit-record-box" : ""
      }`}
    >
      <div>
        <div className="font-mono text-xs">{setting.key}</div>
        {setting.description_key ? (
          <div className="text-xs text-muted-foreground">
            {setting.description_key.replace(/^setting\./, "")}
          </div>
        ) : null}
      </div>
      <div className="flex max-w-xs items-center gap-1">
        <Input
          value={value}
          type={isSecret && !reveal ? "password" : setting.value_type === "int" ? "number" : "text"}
          disabled={saving}
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => void save()}
          placeholder={isSecret && !value ? "(not set)" : undefined}
        />
        {isSecret ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setReveal((r) => !r)}
            aria-label={reveal ? "Hide" : "Show"}
          >
            {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const GROUP_LABELS: Record<string, string> = {
  translation: "Machine translation",
  maps: "Route planning",
  audit: "Audit",
  slot: "Scheduling",
  rate: "Rates",
  import: "Import",
  alert: "Alerts",
};

const GROUP_HINTS: Record<string, string> = {
  translation:
    "Add a Google Cloud Translation API key (that's all you need), or a service-account JSON path + project ID for the v3 client.",
  maps: "Google Maps API key for the route planner (stored encrypted; restrict it by HTTP referrer).",
};

/** Application settings: every parameterised constant, edited in place. */
export function SettingsPage() {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: ["settings"], queryFn: () => listSettings() });
  // Audit deep-link context (?r=<id>&a=<action>&f=<fields>&ar=<auditId>).
  const audit = useAuditHighlight();
  const ghostRecord = isDeletedRecord(audit);

  const items = query.data?.items ?? [];
  const groups = new Map<string, AppSetting[]>();
  for (const setting of items) {
    const prefix = setting.key.split(".")[0] ?? "general";
    const list = groups.get(prefix) ?? [];
    list.push(setting);
    groups.set(prefix, list);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.settings")}</h1>
        <p className="text-sm text-muted-foreground">Edit a value and tab away to save.</p>
      </div>

      {ghostRecord ? (
        <AuditGhostRecord
          entityType={audit.entityType ?? "app_setting"}
          entityId={audit.recordId}
        />
      ) : null}

      {query.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        Array.from(groups.entries()).map(([prefix, settings]) => (
          <div key={prefix} className="rounded-md border">
            <div className="border-b bg-muted/40 px-4 py-2">
              <Label className="text-sm font-semibold">
                {GROUP_LABELS[prefix] ?? prefix}
              </Label>
              {GROUP_HINTS[prefix] ? (
                <p className="mt-0.5 text-xs text-muted-foreground">{GROUP_HINTS[prefix]}</p>
              ) : null}
            </div>
            <div className="px-4">
              {settings.map((setting) => (
                <SettingRow
                  key={setting.key}
                  setting={setting}
                  highlighted={String(setting.id) === audit.recordId}
                />
              ))}
            </div>
          </div>
        ))
      )}

      <MailboxesSection />

      <RestartSection />
    </div>
  );
}
